import './AttachFilesExecutor.scss';
import React, { useEffect, useRef, useState } from 'react';
import { useForm } from 'react-hook-form';
import { IAttachesCreateModel } from '@models/attaches/IAttachesCreateModel';
import { AttachFilesBlock, IAttachFilesBlockRefActions } from '@molecules/AttachFilesBlock/AttachFilesBlock';
import { AttachActionsService } from '@services/actions/AttachActionsService';
import Hint from '@srm/ui/hint';
import ModalContainer, { IModalContainerProps } from '@molecules/ModalContainer/ModalContainer';
import { stringifyError } from '@srm/core/errors';
import { DocumentExecutionService } from '@services/actions/DocumentExecutionService';
import { getUploadingFileLabel } from '@utils/uploadProgress';
import { hasSelectedFiles } from '@organisms/actions/skipUploadedFiles';
import { IAttachFilesExecutorProps } from './IAttachFilesExecutorProps';

export const AttachFilesExecutorModal: React.FC<IAttachFilesExecutorProps> = (props: IAttachFilesExecutorProps) => {
    const [isBusy, setIsBusy] = useState<boolean>(true);
    const [errorText, setErrorText] = useState<string>();
    const [attachesCreateModel, setAttachesCreateModel] = useState<IAttachesCreateModel>();
    const [submitDisabled, setSubmitDisabled] = useState<boolean>(true);
    const [filesSizeExceeded, setFilesSizeExceeded] = useState<boolean>(false);
    const [uploadProgress, setUploadProgress] = useState<number>();
    const [submitButtonLabel, setSubmitButtonLabel] = useState<string>();
    const [isSubmitting, setIsSubmitting] = useState<boolean>(false);
    const childFilesBlock = useRef<IAttachFilesBlockRefActions | null>(null);

    const formMethods = useForm();

    useEffect(() => {
        // Закрытое окно ответ подготовки не применяет.
        let ignore = false;
        setIsBusy(true);
        const service = new AttachActionsService(props.docId, '-1');
        service
            .getAttachInfo(props.action.key)
            .then((res) => {
                if (!ignore) setAttachesCreateModel(res.data);
            })
            .catch(() => {
                if (!ignore) setAttachesCreateModel(undefined);
            })
            .finally(() => {
                if (!ignore) setIsBusy(false);
            });
        return () => {
            ignore = true;
        };
    }, []);

    const onSubmit = async () => {
        if (!childFilesBlock.current?.isValid()) return;

        setErrorText(undefined);
        setIsBusy(true);
        setIsSubmitting(true);
        const uploadingFiles = childFilesBlock.current.getData();
        const hasFilesToUpload = uploadingFiles.files.length > 0;

        if (hasFilesToUpload) {
            setUploadProgress(0);
            setSubmitButtonLabel('Подготавливаю файлы...');
        } else {
            setSubmitButtonLabel('Сохраняю файлы...');
        }

        try {
            const service = new DocumentExecutionService(props.docId);
            await service.attachFiles(
                props.action.key,
                uploadingFiles.attachProperties,
                uploadingFiles.files,
                hasFilesToUpload
                    ? (state) => {
                          setUploadProgress(state.progress);
                          setSubmitButtonLabel(
                              state.progress >= 100 ? 'Сохраняю файлы...' : getUploadingFileLabel(state),
                          );
                      }
                    : undefined,
            );
            if (props.completeHandler) props.completeHandler(true);
        } catch (error) {
            setErrorText(stringifyError(error));
        } finally {
            setIsBusy(false);
            setIsSubmitting(false);
            setUploadProgress(undefined);
            setSubmitButtonLabel(undefined);
        }
    };

    const modalContainerProps: IModalContainerProps = {
        header: 'Присоединить файлы',
        size: props.modalSize,
        showOkButton: true,
        showCancelButton: true,
        okButtonText: props.okButtonText,
        cancelButtonText: props.cancelButtonText,
        loading: isBusy && !isSubmitting,
        loadingLabel: submitButtonLabel ?? 'Готовим форму загрузки…',
        progress: uploadProgress,
        progressLabel: submitButtonLabel,
        closeDisabled: isSubmitting,
        hasUnsavedInput: () => hasSelectedFiles(childFilesBlock.current),
        okButtonDisabled: filesSizeExceeded || submitDisabled,
        errorText: errorText,
        formMethods: formMethods,
        onSubmit: onSubmit,
        onCancel: () => {
            if (props.completeHandler) props.completeHandler(false);
        },
    };

    return (
        <ModalContainer {...modalContainerProps}>
            <div className="attach-files-executor__layout">
                {attachesCreateModel ? (
                    <AttachFilesBlock
                        ref={childFilesBlock}
                        model={attachesCreateModel}
                        onChanged={() => {
                            const uploadingFiles = childFilesBlock.current?.getData();
                            setSubmitDisabled(uploadingFiles?.files?.length === 0);
                        }}
                        onChangeFilesSizeExceeded={(value) => {
                            setFilesSizeExceeded(value);
                        }}
                    />
                ) : !isBusy ? (
                    <Hint
                        role="alert"
                        title="Не удалось загрузить конфигурацию действия для данного типа документов"
                        tone="danger"
                        maxWidth="100%"
                    />
                ) : null}
            </div>
        </ModalContainer>
    );
};
